import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Repeat, Menu } from 'lucide-react';
import { useChatLayout } from '../../components/layout/ChatLayout';
import { RoutineForm } from '../../components/routines';
import type { RoutineFormSubmit } from '../../components/routines';
import { routinesApi } from '../../services/routines-api';
import { handleApiError } from '../../services/api';
import { useTimezone } from '../../contexts/TimezoneContext';
import toast from 'react-hot-toast';

const CreateRoutinePage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile, toggleSidebar } = useChatLayout();
  const { timezone: orgTimezone } = useTimezone();

  const createMutation = useMutation({
    mutationFn: (data: RoutineFormSubmit) =>
      routinesApi.createRoutine({
        name: data.name,
        schedule: data.schedule,
        timezone: data.timezone,
        message: data.message,
        outputChannels: data.outputChannels,
      }),
    onSuccess: () => {
      toast.success('Routine created');
      navigate('/routines');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to create routine');
    },
  });

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/routines')}
            className="p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Repeat className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Create Routine
              </h1>
              <p className="text-muted-foreground">
                A saved prompt that an agent runs on a schedule and delivers to chat plus any channels you choose.
              </p>
            </div>
          </div>
        </div>

        <RoutineForm
          organizationTimezone={orgTimezone}
          submitLabel="Create routine"
          onSubmit={async (state) => {
            await createMutation.mutateAsync(state);
          }}
          onCancel={() => navigate('/routines')}
          busy={createMutation.isPending}
        />
      </div>
    </div>
  );
};

export default CreateRoutinePage;
